// ============================================
// Calibration Runner
// Runs gold standard cases through the Protocol Builder,
// compares output to expected AND to Dr. C's plan,
// scores 4 dimensions: supplements, dosing, labs, monitoring.
// Stores results in Firestore.
// ============================================

import { GOLD_STANDARD_CASES } from '../../data/calibration/goldStandardCases';
import { runProtocolBuilder } from '../agents/protocolBuilder';
import { saveCalibrationRun } from '../firestore/calibrationRunService';
import type { CalibrationRun, CaseResult, GoldStandardCase, DimensionScores } from '../../types/calibrationRun';
import type { Protocol } from '../../types';

const DEVIATION_THRESHOLD = 5; // Flag if recall deviation > 5%

// ── Dosing similarity ──
// Normalize dosage strings for comparison: lowercase, strip spaces, unify units
function normalizeDosage(d: string): string {
  return d
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/daily/gi, '')
    .replace(/with food/gi, '')
    .replace(/with meal/gi, '')
    .replace(/before bed/gi, '')
    .trim();
}

function dosageSimilarity(actual: string, expected: string): number {
  const a = normalizeDosage(actual);
  const e = normalizeDosage(expected);
  if (a === e) return 100;

  // Extract numeric values for comparison
  const aNumbers = a.match(/[\d.]+/g)?.map(Number) ?? [];
  const eNumbers = e.match(/[\d.]+/g)?.map(Number) ?? [];

  if (aNumbers.length === 0 || eNumbers.length === 0) {
    // String similarity fallback: check if key terms overlap
    const aTerms = new Set(a.split(/\s+/));
    const eTerms = new Set(e.split(/\s+/));
    let overlap = 0;
    for (const t of aTerms) { if (eTerms.has(t)) overlap++; }
    return Math.round((overlap / Math.max(aTerms.size, eTerms.size)) * 100);
  }

  // Compare primary numeric value (dosage amount)
  const primaryA = aNumbers[0];
  const primaryE = eNumbers[0];
  if (primaryA === primaryE) return 100;

  // Within 20% = partial match
  const ratio = Math.min(primaryA, primaryE) / Math.max(primaryA, primaryE);
  return Math.round(ratio * 100);
}

// ── Lab order matching ──
function labOrderMatchScore(actual: string[], expected: string[]): number {
  if (expected.length === 0) return actual.length === 0 ? 100 : 80; // no labs expected
  const expectedSet = new Set(expected.map((l) => l.toLowerCase()));
  let matched = 0;
  for (const lab of actual) {
    if (expectedSet.has(lab.toLowerCase())) matched++;
  }
  return Math.round((matched / expected.length) * 100);
}

// ── Monitoring timeline matching ──
// Extract key numbers (days/weeks/months) and key terms
function monitoringMatchScore(protocolSummary: string, expectedTimeline: string): number {
  const summaryLower = protocolSummary.toLowerCase();
  const expectedLower = expectedTimeline.toLowerCase();

  // Extract timeline numbers
  const summaryDays = extractTimelineDays(summaryLower);
  const expectedDays = extractTimelineDays(expectedLower);

  if (summaryDays > 0 && expectedDays > 0) {
    const ratio = Math.min(summaryDays, expectedDays) / Math.max(summaryDays, expectedDays);
    return Math.round(ratio * 100);
  }

  // Fallback: key term overlap
  const keyTerms = ['recheck', 'days', 'weeks', 'months', 'annual', 'follow-up', '30', '60', '90', '120'];
  let matches = 0;
  for (const term of keyTerms) {
    if (summaryLower.includes(term) && expectedLower.includes(term)) matches++;
  }
  return Math.min(100, Math.round((matches / 3) * 100)); // 3+ shared terms = 100%
}

function extractTimelineDays(text: string): number {
  // Look for "X days" or "X weeks" or "X months"
  const dayMatch = text.match(/(\d+)\s*days?/);
  if (dayMatch) return parseInt(dayMatch[1]);
  const weekMatch = text.match(/(\d+)\s*weeks?/);
  if (weekMatch) return parseInt(weekMatch[1]) * 7;
  const monthMatch = text.match(/(\d+)\s*months?/);
  if (monthMatch) return parseInt(monthMatch[1]) * 30;
  // Annual
  if (text.includes('annual')) return 365;
  return 0;
}

/** Score the 4 dimensions for a single case. */
function scoreDimensions(
  gs: GoldStandardCase,
  protocol: Protocol,
  actualIds: string[],
): DimensionScores {
  const plan = gs.drCPlan;

  // 1. Supplement match (same as recall)
  const expectedSet = new Set(plan.products);
  const actualSet = new Set(actualIds);
  const isNegativeControl = plan.products.length === 0;
  let supplementMatch: number;
  if (isNegativeControl) {
    supplementMatch = actualIds.length <= 2 ? 100 : Math.max(0, 100 - (actualIds.length - 2) * 20);
  } else {
    let matched = 0;
    for (const id of actualIds) { if (expectedSet.has(id)) matched++; }
    supplementMatch = Math.round((matched / plan.products.length) * 100);
  }

  // 2. Dosing alignment — average similarity across matched products
  let dosingAlignment = 100;
  if (plan.dosing.length > 0) {
    const dosingScores: number[] = [];
    for (const expected of plan.dosing) {
      if (!actualSet.has(expected.productId)) {
        dosingScores.push(0); // Product not recommended at all
        continue;
      }
      const actualSupp = protocol.supplements.find((s) => s.productId === expected.productId);
      if (actualSupp) {
        dosingScores.push(dosageSimilarity(actualSupp.dosage, expected.dosage));
      } else {
        dosingScores.push(0);
      }
    }
    dosingAlignment = dosingScores.length > 0
      ? Math.round(dosingScores.reduce((a, b) => a + b, 0) / dosingScores.length)
      : 0;
  }

  // 3. Lab order match — extract lab mentions from protocol summary
  const labMentions = extractLabMentions(protocol.summary, plan.labOrders);
  const labOrderMatch = labOrderMatchScore(labMentions, plan.labOrders);

  // 4. Monitoring timeline match
  const monitoringMatch = monitoringMatchScore(protocol.summary, plan.monitoringTimeline);

  return { supplementMatch, dosingAlignment, labOrderMatch, monitoringMatch };
}

/** Extract mentioned lab names from protocol summary text. */
function extractLabMentions(summary: string, knownLabs: string[]): string[] {
  const found: string[] = [];
  const lower = summary.toLowerCase();
  for (const lab of knownLabs) {
    if (lower.includes(lab.toLowerCase())) {
      found.push(lab);
    }
  }
  // Also check for common short forms
  const shortForms: Record<string, string> = {
    'cbc': 'CBC', 'cmp': 'CMP', 'tsh': 'TSH', 'a1c': 'Hemoglobin A1c',
    'lipid': 'Lipid Panel', 'crp': 'hs-CRP', 'vitamin d': 'Vitamin D (25-OH)',
  };
  for (const [short, full] of Object.entries(shortForms)) {
    if (lower.includes(short) && !found.includes(full)) {
      found.push(full);
    }
  }
  return found;
}

/** Run a single gold standard case through Protocol Builder. */
async function runSingleCase(gs: GoldStandardCase): Promise<CaseResult> {
  const start = Date.now();
  const emptyDimensions: DimensionScores = {
    supplementMatch: 0, dosingAlignment: 0, labOrderMatch: 0, monitoringMatch: 0,
  };

  let protocol: Protocol;
  try {
    protocol = await runProtocolBuilder(
      gs.biomarkers,
      gs.goals,
      gs.medications,
      gs.allergies,
      gs.demographics.sex,
      false, // NEVER mock — this is real calibration
    );
  } catch (err) {
    // If Protocol Builder fails, record as 0% match
    return {
      caseId: gs.caseId,
      caseName: gs.name,
      expectedProducts: gs.expectedProducts,
      actualProducts: [],
      matchedProducts: [],
      missingProducts: [...gs.expectedProducts],
      unexpectedProducts: [],
      forbiddenViolations: [],
      recallPercent: 0,
      precisionPercent: 0,
      deviationPercent: 100,
      flagged: true,
      protocolSummary: `ERROR: ${err instanceof Error ? err.message : String(err)}`,
      executionTimeMs: Date.now() - start,
      dimensions: emptyDimensions,
      expectedLabOrders: gs.drCPlan.labOrders,
      actualDosing: [],
      expectedMonitoring: gs.drCPlan.monitoringTimeline,
    };
  }

  const actualIds = protocol.supplements
    .map((s) => s.productId)
    .filter((id): id is string => !!id);

  const expectedSet = new Set(gs.expectedProducts);
  const actualSet = new Set(actualIds);
  const forbiddenSet = new Set(gs.forbiddenProducts);

  const matched = actualIds.filter((id) => expectedSet.has(id));
  const missing = gs.expectedProducts.filter((id) => !actualSet.has(id));
  const unexpected = actualIds.filter((id) => !expectedSet.has(id));
  const forbiddenViolations = actualIds.filter((id) => forbiddenSet.has(id));

  // For the negative control (no expected products), check that
  // the model recommends 2 or fewer (some foundation is acceptable)
  const isNegativeControl = gs.expectedProducts.length === 0;
  let recallPercent: number;
  let deviationPercent: number;

  if (isNegativeControl) {
    recallPercent = actualIds.length <= 2 ? 100 : Math.max(0, 100 - (actualIds.length - 2) * 20);
    deviationPercent = 100 - recallPercent;
  } else {
    recallPercent = gs.expectedProducts.length > 0
      ? Math.round((matched.length / gs.expectedProducts.length) * 100)
      : 100;
    deviationPercent = 100 - recallPercent;
  }

  const precisionPercent = actualIds.length > 0
    ? Math.round((matched.length / actualIds.length) * 100)
    : (gs.expectedProducts.length === 0 ? 100 : 0);

  const flagged = deviationPercent > DEVIATION_THRESHOLD || forbiddenViolations.length > 0;

  // Score 4 dimensions against Dr. C's plan
  const dimensions = scoreDimensions(gs, protocol, actualIds);

  // Extract actual dosing
  const actualDosing = protocol.supplements
    .filter((s) => s.productId)
    .map((s) => ({ productId: s.productId!, dosage: s.dosage }));

  return {
    caseId: gs.caseId,
    caseName: gs.name,
    expectedProducts: gs.expectedProducts,
    actualProducts: actualIds,
    matchedProducts: matched,
    missingProducts: missing,
    unexpectedProducts: unexpected,
    forbiddenViolations,
    recallPercent,
    precisionPercent,
    deviationPercent,
    flagged,
    protocolSummary: protocol.summary,
    executionTimeMs: Date.now() - start,
    dimensions,
    expectedLabOrders: gs.drCPlan.labOrders,
    actualDosing,
    expectedMonitoring: gs.drCPlan.monitoringTimeline,
  };
}

/**
 * Run the full calibration suite.
 * Executes cases sequentially to avoid rate limiting.
 * Calls onProgress after each case completes.
 */
export async function runCalibrationSuite(
  onProgress?: (completed: number, total: number, latest: CaseResult) => void,
): Promise<CalibrationRun> {
  const runId = `cal-${Date.now()}`;
  const startedAt = new Date().toISOString();

  const emptyDimensions: DimensionScores = {
    supplementMatch: 0, dosingAlignment: 0, labOrderMatch: 0, monitoringMatch: 0,
  };

  const run: CalibrationRun = {
    runId,
    startedAt,
    status: 'running',
    modelUsed: 'claude-opus-4-6',
    totalCases: GOLD_STANDARD_CASES.length,
    passedCases: 0,
    failedCases: 0,
    flaggedCases: 0,
    overallRecall: 0,
    overallPrecision: 0,
    driftDetected: false,
    results: [],
    dimensionAverages: emptyDimensions,
  };

  // Save initial running state
  await saveCalibrationRun(run);

  const results: CaseResult[] = [];

  for (let i = 0; i < GOLD_STANDARD_CASES.length; i++) {
    const gs = GOLD_STANDARD_CASES[i];
    const result = await runSingleCase(gs);
    results.push(result);

    if (result.flagged) {
      run.flaggedCases++;
    }
    if (result.deviationPercent <= DEVIATION_THRESHOLD && result.forbiddenViolations.length === 0) {
      run.passedCases++;
    } else {
      run.failedCases++;
    }

    onProgress?.(i + 1, GOLD_STANDARD_CASES.length, result);
  }

  // Compute aggregates
  const totalRecall = results.reduce((sum, r) => sum + r.recallPercent, 0);
  const totalPrecision = results.reduce((sum, r) => sum + r.precisionPercent, 0);
  const n = results.length;

  run.results = results;
  run.overallRecall = Math.round(totalRecall / n);
  run.overallPrecision = Math.round(totalPrecision / n);
  run.driftDetected = run.flaggedCases > 0;
  run.status = 'completed';
  run.completedAt = new Date().toISOString();

  // Compute dimension averages
  run.dimensionAverages = {
    supplementMatch: Math.round(results.reduce((s, r) => s + r.dimensions.supplementMatch, 0) / n),
    dosingAlignment: Math.round(results.reduce((s, r) => s + r.dimensions.dosingAlignment, 0) / n),
    labOrderMatch: Math.round(results.reduce((s, r) => s + r.dimensions.labOrderMatch, 0) / n),
    monitoringMatch: Math.round(results.reduce((s, r) => s + r.dimensions.monitoringMatch, 0) / n),
  };

  // Save final state
  await saveCalibrationRun(run);

  return run;
}
